import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  Grid,
  Alert,
  CircularProgress,
  Button,
  Divider,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  MoreVert,
  Search,
  Delete,
  Edit,
  PersonAdd,
  Share,
  CheckCircle,
} from "@mui/icons-material";
import { useReadAclQuery } from "api/endpoints/acl";
import { UsersAcl } from "store/reducers/acl/acl.type";
import {
  useDeleteUserMutation,
  useReadManyUserQuery,
  useApproveUserMutation,
  useReadProfileQuery,
} from "api/endpoints";
import { ROUTE_PATH } from "router/router-constant/router.constant";
import { useNavigate, useParams } from "react-router-dom";
import { ConfirmationDialogComponent } from "lib/components/ui/confirmation-dialog";
import { InviteDialogComponent } from "lib/components/ui/invite-dialog";
import { UserListProps } from "./user-list.type";

export const UserListComponent = ({
  pageTitle,
  showActions,
  handleRowClick,
  columnsPattern,
  backUrl,
}: UserListProps) => {
  const navigate = useNavigate();
  const { departmentId = "" } = useParams();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<string>("");
  const [selectedUserApproved, setSelectedUserApproved] =
    useState<boolean>(true);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);

  // Confirmation dialog states
  const [confirmationDialog, setConfirmationDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmText: string;
    confirmColor:
      | "primary"
      | "secondary"
      | "error"
      | "success"
      | "warning"
      | "info";
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: "",
    message: "",
    confirmText: "تایید",
    confirmColor: "primary",
    onConfirm: () => {},
  });

  const { acl: aclData } = useReadAclQuery<UsersAcl>(`users`);
  const acl = { ...aclData };

  if (!showActions && acl) {
    acl.delete = false;
    acl.update = false;
    acl.create = false;
  }

  const [deleteUser] = useDeleteUserMutation();
  const [approveUser] = useApproveUserMutation();
  const { data: profileData } = useReadProfileQuery();

  const {
    data: usersList,
    isLoading,
    error,
    refetch,
  } = useReadManyUserQuery({
    query: {
      page: String(page + 1),
      limit: String(rowsPerPage),
      ...(search && { search }),
      ...(departmentId && { department: departmentId }),
    },
  });

  const users = usersList?.users || [];
  const totalCount = usersList?.count || 0;

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCreateUser = (e: any) => {
    e.stopPropagation();
    navigate(ROUTE_PATH.users.new.path);
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    userId: string,
    isApproved: boolean
  ) => {
    setMenuAnchor(event.currentTarget);
    setSelectedUser(userId);
    setSelectedUserApproved(isApproved);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedUser("");
    setSelectedUserApproved(true);
  };

  const showConfirmationDialog = (
    title: string,
    message: string,
    confirmText: string,
    confirmColor:
      | "primary"
      | "secondary"
      | "error"
      | "success"
      | "warning"
      | "info",
    onConfirm: () => void
  ) => {
    setConfirmationDialog({
      isOpen: true,
      title,
      message,
      confirmText,
      confirmColor,
      onConfirm,
    });
  };

  const closeConfirmationDialog = () => {
    setConfirmationDialog((prev) => ({ ...prev, isOpen: false }));
  };

  const handleEditUser = () => {
    if (!selectedUser) return;
    navigate(ROUTE_PATH.users.edit.pathBuilder(selectedUser));
    handleMenuClose();
  };

  const handleDeleteUser = () => {
    if (!selectedUser) return;

    showConfirmationDialog(
      "حذف کاربر",
      "آیا از حذف این کاربر اطمینان دارید؟ این عمل قابل بازگشت نیست.",
      "حذف",
      "error",
      async () => {
        try {
          await deleteUser({
            params: {
              userId: selectedUser,
            },
          }).unwrap();
          refetch();
          handleMenuClose();
          closeConfirmationDialog();
        } catch (error) {
          console.error("Failed to delete user:", error);
          closeConfirmationDialog();
        }
      }
    );
  };

  const handleApproveUser = () => {
    if (!selectedUser) return;

    showConfirmationDialog(
      "تایید کاربر",
      "آیا از تایید این کاربر اطمینان دارید؟ پس از تایید، کاربر می‌تواند وارد سیستم شود.",
      "تایید",
      "success",
      async () => {
        try {
          await approveUser({
            params: {
              userId: selectedUser,
            },
          }).unwrap();
          refetch();
          handleMenuClose();
          closeConfirmationDialog();
        } catch (error) {
          console.error("Failed to approve user:", error);
          closeConfirmationDialog();
        }
      }
    );
  };

  const handleInvite = () => {
    setInviteDialogOpen(true);
  };

  const getInvitationLink = () => {
    const userId = profileData?._id;
    if (!userId) return "";
    return `${window.location.origin}${ROUTE_PATH.auth.register.path}?refId=${userId}`;
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        خطا در بارگذاری لیست کاربران
      </Alert>
    );
  }

  return acl?.read ? (
    <Box p={{ xs: 2, sm: 3 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        sx={{
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 0 },
          alignItems: { xs: "stretch", sm: "center" }
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ 
          textAlign: { xs: "center", sm: "left" },
          fontSize: { xs: "1.5rem", sm: "2rem" }
        }}>
          {pageTitle}
        </Typography>
        <Box 
          display="flex" 
          gap={2}
          sx={{
            flexDirection: { xs: "column", sm: "row" },
            width: { xs: "100%", sm: "auto" }
          }}
        >
          <Button
            variant="outlined"
            startIcon={<Share className="icon-btn" />}
            onClick={handleInvite}
            sx={{ 
              borderRadius: 2,
              width: { xs: "100%", sm: "auto" },
              minHeight: { xs: "48px", sm: "36px" }
            }}
          >
            دعوت کاربر جدید
          </Button>
          {acl.create && (
            <Button
              variant="contained"
              startIcon={<PersonAdd className="icon-btn" />}
              onClick={handleCreateUser}
              sx={{ 
                borderRadius: 2,
                width: { xs: "100%", sm: "auto" },
                minHeight: { xs: "48px", sm: "36px" }
              }}
            >
              افزودن کاربر
            </Button>
          )}
        </Box>
      </Box>

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Search */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              placeholder="جستجو در نام یا شماره تلفن..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>

        {/* Enhanced Table */}
        <TableContainer sx={{ 
          overflowX: "auto",
          "&::-webkit-scrollbar": {
            height: 8,
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "grey.100",
            borderRadius: 4,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "grey.400",
            borderRadius: 4,
            "&:hover": {
              backgroundColor: "grey.500",
            },
          },
        }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "grey.50" }}>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  color: "text.primary",
                  fontSize: { xs: "0.875rem", sm: "0.95rem" },
                  padding: { xs: "8px 4px", sm: "16px" }
                }}>
                  نام کامل
                </TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  color: "text.primary",
                  fontSize: { xs: "0.875rem", sm: "0.95rem" },
                  padding: { xs: "8px 4px", sm: "16px" }
                }}>
                  شماره تلفن
                </TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  color: "text.primary",
                  fontSize: { xs: "0.875rem", sm: "0.95rem" },
                  padding: { xs: "8px 4px", sm: "16px" }
                }}>
                  نقش
                </TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  color: "text.primary",
                  fontSize: { xs: "0.875rem", sm: "0.95rem" },
                  padding: { xs: "8px 4px", sm: "16px" }
                }}>
                  نام معرف
                </TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  color: "text.primary",
                  fontSize: { xs: "0.875rem", sm: "0.95rem" },
                  padding: { xs: "8px 4px", sm: "16px" }
                }}>
                  وضعیت
                </TableCell>
                {showActions && (
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                      textAlign: "center",
                      fontSize: { xs: "0.875rem", sm: "0.95rem" },
                      padding: { xs: "8px 4px", sm: "16px" }
                    }}
                  >
                    عملیات
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow
                  key={user._id}
                  sx={{
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                    "&:nth-of-type(even)": {
                      backgroundColor: "grey.25",
                    },
                    transition: "background-color 0.2s ease-in-out",
                  }}
                >
                  <TableCell sx={{ 
                    fontSize: { xs: "0.875rem", sm: "0.95rem" },
                    padding: { xs: "8px 4px", sm: "16px" }
                  }}>{user.fullName}</TableCell>
                  <TableCell sx={{ 
                    fontSize: { xs: "0.875rem", sm: "0.95rem" },
                    padding: { xs: "8px 4px", sm: "16px" }
                  }} dir="ltr">{user.phoneNumber}</TableCell>
                  <TableCell sx={{ 
                    fontSize: { xs: "0.875rem", sm: "0.95rem" },
                    padding: { xs: "8px 4px", sm: "16px" }
                  }}>
                    {user.role === "admin" ? "مدیر" : "کاربر"}
                  </TableCell>
                  <TableCell sx={{ 
                    fontSize: { xs: "0.875rem", sm: "0.95rem" },
                    padding: { xs: "8px 4px", sm: "16px" }
                  }}>{user.referrerName || "-"}</TableCell>
                  <TableCell sx={{ 
                    fontSize: { xs: "0.875rem", sm: "0.95rem" },
                    padding: { xs: "8px 4px", sm: "16px" }
                  }}>
                    {user.isApproved ? (
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        color="success.main"
                      >
                        <CheckCircle fontSize="small" />
                        <Typography variant="body2">تایید شده</Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="warning.dark">
                        در انتظار تایید
                      </Typography>
                    )}
                  </TableCell>
                  {showActions && (
                    <TableCell sx={{ 
                      textAlign: "center",
                      padding: { xs: "8px 4px", sm: "16px" }
                    }}>
                      <IconButton
                        onClick={(e) =>
                          handleMenuOpen(e, user._id, user.isApproved)
                        }
                        sx={{
                          backgroundColor: "action.hover",
                          "&:hover": {
                            backgroundColor: "primary.100",
                            color: "primary.main",
                            transform: "scale(1.05)",
                          },
                          transition: "all 0.2s ease-in-out",
                          borderRadius: 2,
                          width: { xs: 36, sm: 40 },
                          height: { xs: 36, sm: 40 },
                        }}
                      >
                        <MoreVert sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" } }} />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="تعداد در صفحه:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} از ${count !== -1 ? count : to}`
          }
        />

        {/* Enhanced Action Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              minWidth: 200,
              borderRadius: 2,
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              border: "1px solid rgba(0,0,0,0.08)",
            },
          }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          {/* Approve Section */}
          {acl?.update && !selectedUserApproved && (
            <>
              <MenuItem
                onClick={handleApproveUser}
                sx={{
                  py: 1.5,
                  "&:hover": {
                    backgroundColor: "success.50",
                  },
                }}
              >
                <ListItemIcon>
                  <CheckCircle color="success" />
                </ListItemIcon>
                <ListItemText
                  primary="تایید کاربر"
                  secondary="کاربر می‌تواند وارد شود"
                  primaryTypographyProps={{
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    color: "success.main",
                  }}
                  secondaryTypographyProps={{
                    fontSize: "0.75rem",
                    color: "text.secondary",
                  }}
                />
              </MenuItem>
              <Divider />
            </>
          )}

          {/* Edit Section */}
          {acl?.update && (
            <MenuItem
              onClick={handleEditUser}
              sx={{
                py: 1.5,
                "&:hover": {
                  backgroundColor: "primary.50",
                },
              }}
            >
              <ListItemIcon>
                <Edit color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="ویرایش کاربر"
                secondary="اطلاعات کاربر را ویرایش کنید"
                primaryTypographyProps={{
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  color: "primary.main",
                }}
                secondaryTypographyProps={{
                  fontSize: "0.75rem",
                  color: "text.secondary",
                }}
              />
            </MenuItem>
          )}

          {acl?.update && acl?.delete && <Divider />}

          {/* Danger Zone */}
          {acl?.delete && (
            <MenuItem
              onClick={handleDeleteUser}
              sx={{
                py: 1.5,
                "&:hover": {
                  backgroundColor: "error.50",
                },
              }}
            >
              <ListItemIcon>
                <Delete color="error" />
              </ListItemIcon>
              <ListItemText
                primary="حذف کاربر"
                secondary="این عمل قابل بازگشت نیست"
                primaryTypographyProps={{
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  color: "error.main",
                }}
                secondaryTypographyProps={{
                  fontSize: "0.75rem",
                  color: "error.light",
                }}
              />
            </MenuItem>
          )}
        </Menu>

        {/* Confirmation Dialog */}
        <ConfirmationDialogComponent
          isOpen={confirmationDialog.isOpen}
          onClose={closeConfirmationDialog}
          onConfirm={confirmationDialog.onConfirm}
          title={confirmationDialog.title}
          message={confirmationDialog.message}
          confirmText={confirmationDialog.confirmText}
          confirmColor={confirmationDialog.confirmColor}
        />

        {/* Invite Dialog */}
        <InviteDialogComponent
          isOpen={inviteDialogOpen}
          onClose={() => setInviteDialogOpen(false)}
          invitationLink={getInvitationLink()}
        />
      </Paper>
    </Box>
  ) : (
    <></>
  );
};
